import { Plus, LogOut, Layers, ArrowUpRight } from 'lucide-react';
import { BulkRecordModal } from '@/components/BulkRecordModal';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useActiveNavigationScroll } from '@/lib/navigation';
import { usePrivacy } from '@/lib/privacy';
import { Button, cx } from '@/components/ui';
import { NotificationBell } from '@/components/NotificationBell';
import { RecordModal } from '@/components/RecordModal';
import { VerifyEmailBanner } from '@/components/VerifyEmailBanner';
import { PrivacyPrompt, PrivacyToggle } from '@/components/PrivacyToggle';
import { ToastHost, useToast } from '@/components/ToastHost';
import { SearchIcon, TabIcon, type TabName } from '@/components/icons';
import logo from '@/assets/sterling_logo.png';
import s from './AppShell.module.css';

type NavigationItem = { to: string; label: string; icon: TabName; end?: boolean };

/** One catalogue for desktop navigation and the complete mobile bar. */
const NAV: NavigationItem[] = [
  { to: '/app', label: 'Overview', icon: 'overview', end: true },
  { to: '/app/accounts', label: 'Accounts', icon: 'accounts' },
  { to: '/app/transactions', label: 'Transactions', icon: 'transactions' },
  { to: '/app/friends', label: 'Friends', icon: 'friends' },
  { to: '/app/budgets', label: 'Budgets', icon: 'budgets' },
  { to: '/app/analytics', label: 'Analytics', icon: 'analytics' },
  { to: '/app/advisor', label: 'Advisor', icon: 'advisor' },
  { to: '/app/investments', label: 'Investments', icon: 'investments' },
  { to: '/app/settings', label: 'Settings', icon: 'settings' },
];

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { masked } = usePrivacy();
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const bottomNavRef = useActiveNavigationScroll(`${location.pathname}:${user?.role}`);
  const [menuOpen, setMenuOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const navigation: NavigationItem[] =
    user?.role === 'ADMIN'
      ? [...NAV, { to: '/app/admin', label: 'Admin portal', icon: 'admin' }]
      : NAV;

  // Screens with a viewport-sized pane use the actual header height, including
  // the installed app's safe-area inset, rather than a desktop-sized constant.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--header-h');
    const measure = () =>
      root.style.setProperty('--header-h', `${header.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    measure();
    return () => {
      observer.disconnect();
      if (previous) root.style.setProperty('--header-h', previous);
      else root.style.removeProperty('--header-h');
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Reserve the bar's real height, including the home-indicator safe area.
  // The single-row navigation scrolls horizontally above the home indicator.
  useEffect(() => {
    const bar = bottomNavRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--bottom-nav-h');
    const measure = () => {
      const bounds = bar.parentElement!.getBoundingClientRect();
      root.style.setProperty(
        '--bottom-nav-h',
        `${bounds.height ? window.innerHeight - bounds.top : 0}px`,
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (previous) root.style.setProperty('--bottom-nav-h', previous);
      else root.style.removeProperty('--bottom-nav-h');
    };
  }, [location.pathname, user?.role]);

  function navigationLinks(mobile: boolean) {
    return navigation.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          cx(
            mobile ? s.bottomLink : s.navLink,
            isActive && (mobile ? s.bottomLinkActive : s.navLinkActive),
          )
        }
      >
        <TabIcon name={item.icon} className={s.navigationIcon} />
        {item.label}
      </NavLink>
    ));
  }

  // Close the account menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // The accent is a per-user preference; push it into the token so every
  // component picks it up without prop drilling.
  useEffect(() => {
    document.documentElement.style.setProperty('--accent', user?.accentColor || '#8e1537');
  }, [user?.accentColor]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/app/transactions?q=${encodeURIComponent(q)}`);
    setSearch('');
  }

  return (
    <div className={s.shell}>
      <a href="#main-content" className={s.skipLink}>
        Skip to content
      </a>
      <aside className={s.sidebar}>
        <NavLink to="/app" className={s.brand} aria-label="Sterling home">
          <img src={logo} alt="" className={s.logo} />
          <div>
            <div className={s.wordmark}>
              Sterling<span>.</span>
            </div>
            <div className={s.byline}>Your money, in perspective</div>
          </div>
        </NavLink>
        <div className={s.navLabel}>Workspace</div>
        <nav className={s.nav} aria-label="Main">
          {navigationLinks(false)}
        </nav>
        <div className={s.sidebarBottom}>
          <div className={s.sidebarTip}>
            <Layers size={22} aria-hidden="true" />
            <strong>Catch up in one go</strong>
            <p>Record several transactions together, in one place.</p>
            <button onClick={() => setBulkOpen(true)}>
              Bulk record <ArrowUpRight size={16} aria-hidden="true" />
            </button>
          </div>
          <span className={s.byline}>Sterling by Cardinale</span>
        </div>
      </aside>
      <header ref={headerRef} className={s.header}>
        <NavLink to="/app" className={s.mobileBrand} aria-label="Sterling home">
          <img src={logo} alt="" />{' '}
          <span>
            Sterling<span className={s.brandDot}>.</span>
          </span>
        </NavLink>
        <div className={s.headerContext}>
          <span>Your workspace</span>
          <strong>
            {navigation.find((item) =>
              item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
            )?.label ?? 'Sterling'}
          </strong>
        </div>
        <div className={s.actions}>
          <form className={s.search} onSubmit={submitSearch} role="search">
            <SearchIcon />
            <input
              className={s.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search transactions"
            />
          </form>

          <NotificationBell />
          <PrivacyToggle className={s.privacy} />

          <Button
            variant="primary"
            className={s.record}
            aria-label="Record a transaction"
            onClick={() => setRecordOpen(true)}
          >
            <Plus size={18} aria-hidden="true" />
            <span className={s.recordLabel}>Record</span>
          </Button>

          {/*
            The menu lives INSIDE the ref'd wrapper deliberately. When it sat
            outside, the outside-click handler saw mousedown on a menu item as
            a click elsewhere, closed the menu, and unmounted the button before
            its click event could fire — so neither item did anything.
          */}
          <div ref={menuRef} className={s.menuWrap}>
            <button
              type="button"
              className={s.avatar}
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Account menu"
            >
              {user ? initials(user.name) : '··'}
            </button>

            {menuOpen && user ? (
              <div className={s.menu} role="menu">
                <div className={s.menuHead}>
                  <div className={s.menuName}>{user.name}</div>
                  <div className={s.menuEmail}>{user.email}</div>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className={s.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                >
                  <LogOut size={16} aria-hidden="true" /> Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className={s.main}>
        <VerifyEmailBanner />
        {/*
          Keyed on the mask so flipping private mode remounts the current view.
          The formatters are plain functions reading a module flag — nothing
          below subscribes to it — so without this the figures would only
          change on the next unrelated render.
        */}
        <Outlet
          key={masked ? 'masked' : 'shown'}
          context={{
            openRecord: () => setRecordOpen(true),
            openBulkRecord: () => setBulkOpen(true),
            toast,
          }}
        />
      </main>

      <div className={s.bottomBar}>
        <nav ref={bottomNavRef} className={s.bottomNav} aria-label="Main">
          {navigationLinks(true)}
        </nav>
      </div>

      {recordOpen ? (
        <RecordModal
          onBulk={() => {
            setRecordOpen(false);
            setBulkOpen(true);
          }}
          onClose={() => setRecordOpen(false)}
          onSaved={(message) => {
            setRecordOpen(false);
            toast.show(message);
          }}
        />
      ) : null}

      {bulkOpen ? (
        <BulkRecordModal
          onClose={() => setBulkOpen(false)}
          onSaved={(message) => {
            setBulkOpen(false);
            toast.show(message);
          }}
        />
      ) : null}
      <PrivacyPrompt />

      <ToastHost toast={toast} />
    </div>
  );
}

/** Page title block, used by each view so the header row stays consistent. */
export function PageHead({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={s.pageHead}>
      <div>
        <h1 className={s.pageTitle}>{title}</h1>
        {subtitle ? <div className={s.pageSub}>{subtitle}</div> : null}
      </div>
      {action ? <div className={s.rangeSwitch}>{action}</div> : null}
    </div>
  );
}
